import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';

import { GET_SEATING_DATA } from 'store/queries';
import { legShape } from 'constants/propTypes';
import { Spinner } from 'admin-components/components';
import { Row, Column } from 'components/shared/Layout';
import TrainMap from 'components/shared/TrainMap';
import SeatMapSvg from 'components/shared/SeatMap/SeatMapSvg';
import { SEAT_PRIORITY_CODE } from 'constants/seating';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { isSeatAvailable } from 'utils/seating/seat';
import { getSeatMapUrl } from 'utils/seating/seatMap';

const noop = () => {};

const TrainOccupancy = ({
  leg: {
    serviceName,
    origin,
    destination,
    serviceType,
    timing: { departureDate }
  },
  shouldFetchAssistancesPerCoaches = false
}) => {
  const [selectedCoach, setSelectedCoach] = useState();

  const { loading, error, data } = useQuery(GET_SEATING_DATA, {
    variables: {
      serviceId: serviceName,
      destination: destination.uic,
      origin: origin.uic,
      travelDate: departureDate
    },
    fetchPolicy: 'network-only'
  });

  if (loading) {
    return (
      <Row span={16}>
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  const carriages = data?.getSeatingData?.carriages ?? [];

  if (error || carriages.length === 0) {
    return <FormattedMessage id="seatmap.svgError" />;
  }

  const currentCoach = selectedCoach ?? carriages[0].carriageNumber;
  const carriage = carriages.find(({ carriageNumber }) => carriageNumber === currentCoach);
  const templateName = carriage.template?.split('/').pop();
  const isERTrain = serviceType.code === serviceTypeCodes.GREENSPEED;

  const availableSeats = carriage.seats.filter(isSeatAvailable).map((seat) => ({ ...seat, coach: currentCoach }));
  const prioritySeats = carriage.seats.filter((seat) => seat.propertyCodes.includes(SEAT_PRIORITY_CODE));

  return (
    <>
      <TrainMap
        journey={{ trainNumber: serviceName, origin, destination, timing: { date: departureDate }, classOfService: {} }}
        currentCoach={currentCoach}
        selectCarriage={setSelectedCoach}
        showAllCoaches
        shouldFetchAssistancesPerCoaches={shouldFetchAssistancesPerCoaches}
      />
      {templateName ? (
        <SeatMapSvg
          templateName={templateName}
          url={getSeatMapUrl(templateName)}
          currentCoach={currentCoach}
          availableSeats={availableSeats}
          prioritySeats={prioritySeats}
          seats={[]}
          originalSeats={[]}
          seatsData={[]}
          isERTrain={isERTrain}
          onSelectNewSeat={noop}
          onSelectNewSeats={noop}
          onSelectNewPassenger={noop}
          onRemovePassengerSelection={noop}
          onShowUnavailableSeatReason={noop}
        />
      ) : (
        <FormattedMessage id="seatmap.svgError" />
      )}
    </>
  );
};

TrainOccupancy.propTypes = {
  leg: legShape.isRequired,
  shouldFetchAssistancesPerCoaches: PropTypes.bool
};

export default TrainOccupancy;
